import { useState } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Autocomplete from "@mui/material/Autocomplete";
import TextFieldMUI from "@mui/material/TextField";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { DialogActions } from "@mui/material";
import ModalBasicLayout from "../../Modal/ModalBasicLayout";
import ContainerModalActions from "../../Containers/ContainerModalActions";
import Button from "../../Buttons/Button";
import { useTranslationApp } from "../../../lib/i18next";
import {
  BUTTON_GO_BACK,
  MODAL_EDIT_URL_PARAMETERS_TITLE,
  MODAL_EDIT_URL_PARAMETERS_DESCRIPTION,
  MODAL_EDIT_URL_PARAMETERS_UTM_SECTION_LABEL,
  MODAL_EDIT_URL_PARAMETERS_CUSTOM_SECTION_LABEL,
  MODAL_EDIT_URL_PARAMETERS_PREVIEW_LABEL,
  MODAL_EDIT_URL_PARAMETERS_ENTER_PARAM_NAME,
  MODAL_EDIT_URL_PARAMETERS_ENTER_PARAM_VALUE,
  MODAL_EDIT_URL_PARAMETERS_BUTTON_APPLY,
  MODAL_EDIT_URL_PARAMETERS_CUSTOM_KEY_RESERVED,
  URL_EXAMPLE_PLACEHOLDER,
  BUTTON_ADD,
} from "../../../i18n/keysTranslations";
import { INPUT_COLOR_BORDER } from "../../../utils/colors";
import { BORDER_RADIUS } from "../../../utils/constants";
import {
  PLATFORM_REPLACE_VARIABLES,
  getFixedUtmKeysForPlatform,
  getUtmKeysReservedForCustomParams,
} from "./utils/utmParametersConfig";
import { buildUtmString, getInitialUtmParams } from "./utils/utmParameters";

const UTM_FIXED_FIELD_DEFS = [
  { key: "utm_source", label: "utm_source" },
  { key: "utm_medium", label: "utm_medium" },
  { key: "utm_id", label: "utm_id" },
  { key: "utm_campaign", label: "utm_campaign" },
  { key: "utm_content", label: "utm_content" },
];

const buildPreviewParams = ({ fixedValues, customFields, platform }) => {
  const params = {};
  const fixedKeys = getFixedUtmKeysForPlatform(platform);

  fixedKeys.forEach((key) => {
    const val = fixedValues[key];
    if (val !== undefined && val !== null && val !== "") {
      params[key] = val;
    }
  });

  customFields.forEach(({ key, value }) => {
    if (key && key.trim()) {
      params[key.trim()] = value || "";
    }
  });

  return params;
};

const ModalEditUrlParameters = ({
  modalOpen,
  onCloseModal = () => {},
  currentValue = "",
  platform,
  onApply = () => {},
  googleUtmNames,
}) => {
  const { t } = useTranslationApp();

  const replaceVariables = PLATFORM_REPLACE_VARIABLES[platform] || [];

  const { fixedParams, customParams } = getInitialUtmParams({
    currentValue,
    platform,
    googleUtmNames,
  });

  const fixedKeysForPlatform = getFixedUtmKeysForPlatform(platform);
  const visibleFixedFields = UTM_FIXED_FIELD_DEFS.filter(({ key }) =>
    fixedKeysForPlatform.includes(key)
  );
  const reservedCustomKeysLower = getUtmKeysReservedForCustomParams(
    platform
  ).map((k) => k.toLowerCase());

  const defaultValuesFixed = Object.fromEntries(
    fixedKeysForPlatform.map((key) => [key, fixedParams[key] ?? ""])
  );

  const { control, watch, setValue, getValues } = useForm({
    defaultValues: {
      ...defaultValuesFixed,
      customParams: customParams.length > 0 ? customParams : [],
    },
    mode: "onChange",
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "customParams",
  });

  const watchedValues = watch();
  const fixedWatched = Object.fromEntries(
    fixedKeysForPlatform.map((key) => [key, watchedValues[key] ?? ""])
  );
  const customWatched = watchedValues.customParams || [];

  const previewParams = buildPreviewParams({
    fixedValues: fixedWatched,
    customFields: customWatched,
    platform,
  });
  const previewString = buildUtmString(previewParams);

  const [localErrors, setLocalErrors] = useState({});

  const validateCustomKeys = (customFields) => {
    const newErrors = {};
    customFields.forEach((field, index) => {
      const trimmed = (field.key || "").trim().toLowerCase();
      if (field.key && reservedCustomKeysLower.includes(trimmed)) {
        newErrors[index] = t(MODAL_EDIT_URL_PARAMETERS_CUSTOM_KEY_RESERVED);
      }
    });
    return newErrors;
  };

  const onSubmit = () => {
    const values = getValues();
    const customFields = values.customParams || [];
    const validationErrors = validateCustomKeys(customFields);

    if (Object.keys(validationErrors).length > 0) {
      setLocalErrors(validationErrors);
      return;
    }

    const fixedObj = {};
    fixedKeysForPlatform.forEach((key) => {
      fixedObj[key] = values[key];
    });

    const customObj = {};
    customFields.forEach(({ key, value }) => {
      if (key && key.trim()) {
        customObj[key.trim()] = value || "";
      }
    });

    const allParams = { ...fixedObj, ...customObj };
    const result = buildUtmString(allParams);
    onApply(result);
    onCloseModal();
  };

  const handleCustomKeyChange = (index, newKey) => {
    setValue(`customParams.${index}.key`, newKey);
    if (reservedCustomKeysLower.includes((newKey || "").trim().toLowerCase())) {
      setLocalErrors((prev) => ({
        ...prev,
        [index]: t(MODAL_EDIT_URL_PARAMETERS_CUSTOM_KEY_RESERVED),
      }));
    } else {
      setLocalErrors((prev) => {
        const next = { ...prev };
        delete next[index];
        return next;
      });
    }
  };

  const sxAutocomplete = {
    flex: 1,
    "& .MuiFilledInput-root": {
      backgroundColor: "fields.background",
      border: `1px solid ${INPUT_COLOR_BORDER}`,
      borderRadius: `${BORDER_RADIUS * 4}px`,
      boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.05)",
      "&:before, &:after": { display: "none" },
    },
  };

  const sxTextField = {
    flex: 1,
    "& .MuiFilledInput-root": {
      height: "72px",
      backgroundColor: "fields.background",
      border: `1px solid ${INPUT_COLOR_BORDER}`,
      borderRadius: `${BORDER_RADIUS * 4}px`,
      boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.05)",
      "&:before, &:after": { display: "none" },
    },
  };

  return (
    <ModalBasicLayout
      modalOpen={modalOpen}
      onCloseModal={onCloseModal}
      fullWidth
      maxWidth="sm"
      title={t(MODAL_EDIT_URL_PARAMETERS_TITLE)}
      description={t(MODAL_EDIT_URL_PARAMETERS_DESCRIPTION)}
      scroll="paper"
    >
      <Box
        id="utm-modal-form"
        component="form"
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onSubmit();
        }}
        sx={{ display: "flex", flexDirection: "column", gap: 3 }}
      >
        {/* UTM fixed params */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          <Typography variant="body1" sx={{ fontWeight: 600 }}>
            {t(MODAL_EDIT_URL_PARAMETERS_UTM_SECTION_LABEL)}
          </Typography>

          {visibleFixedFields.map(({ key, label }) => (
            <Box
              key={key}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
              }}
            >
              <Box sx={{ minWidth: 130 }}>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {label}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                =
              </Typography>
              <Autocomplete
                freeSolo
                options={replaceVariables}
                value={watchedValues[key] ?? ""}
                onChange={(_, newValue) => {
                  setValue(key, newValue ?? "");
                }}
                onInputChange={(_, newValue) => {
                  setValue(key, newValue ?? "");
                }}
                sx={sxAutocomplete}
                renderInput={(params) => (
                  <TextFieldMUI
                    {...params}
                    variant="filled"
                    hiddenLabel
                    size="small"
                    InputProps={{
                      ...params.InputProps,
                      disableUnderline: true,
                    }}
                  />
                )}
              />
            </Box>
          ))}
        </Box>

        {/* Custom params */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          <Typography variant="body1" sx={{ fontWeight: 600 }}>
            {t(MODAL_EDIT_URL_PARAMETERS_CUSTOM_SECTION_LABEL)}
          </Typography>

          {fields.map((field, index) => (
            <Box
              key={field.id}
              sx={{ display: "flex", alignItems: "center", gap: 1 }}
            >
              <TextFieldMUI
                variant="filled"
                hiddenLabel
                size="small"
                placeholder={t(MODAL_EDIT_URL_PARAMETERS_ENTER_PARAM_NAME)}
                value={watchedValues.customParams?.[index]?.key ?? ""}
                onChange={(e) => handleCustomKeyChange(index, e.target.value)}
                error={!!localErrors[index]}
                helperText={localErrors[index] || ""}
                sx={sxTextField}
                InputProps={{ disableUnderline: true }}
              />
              <Typography variant="body2" color="text.secondary">
                =
              </Typography>
              <Autocomplete
                freeSolo
                options={replaceVariables}
                value={watchedValues.customParams?.[index]?.value ?? ""}
                onChange={(_, newValue) => {
                  setValue(`customParams.${index}.value`, newValue ?? "");
                }}
                onInputChange={(_, newValue) => {
                  setValue(`customParams.${index}.value`, newValue ?? "");
                }}
                sx={sxAutocomplete}
                renderInput={(params) => (
                  <TextFieldMUI
                    {...params}
                    variant="filled"
                    hiddenLabel
                    size="small"
                    placeholder={t(MODAL_EDIT_URL_PARAMETERS_ENTER_PARAM_VALUE)}
                    InputProps={{
                      ...params.InputProps,
                      disableUnderline: true,
                    }}
                  />
                )}
              />
              <IconButton
                size="small"
                onClick={() => {
                  remove(index);
                  setLocalErrors((prev) => {
                    const next = {};
                    Object.entries(prev).forEach(([k, v]) => {
                      const ki = parseInt(k, 10);
                      if (ki < index) next[ki] = v;
                      else if (ki > index) next[ki - 1] = v;
                    });
                    return next;
                  });
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}

          <Box>
            <Button
              color="secondary"
              type="button"
              startIcon={<AddIcon />}
              onClick={() => append({ key: "", value: "" })}
              sx={{ width: "auto" }}
            >
              {t(BUTTON_ADD)}
            </Button>
          </Box>
        </Box>

        {/* Preview */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <Typography variant="body1" sx={{ fontWeight: 600 }}>
            {t(MODAL_EDIT_URL_PARAMETERS_PREVIEW_LABEL)}
          </Typography>
          <Box
            sx={{
              p: 2,
              borderRadius: `${BORDER_RADIUS * 4}px`,
              backgroundColor: "background.paperSecondary",
              border: `1px solid ${INPUT_COLOR_BORDER}`,
              display: "flex",
              flexWrap: "wrap",
              gap: 0.5,
              alignItems: "center",
            }}
          >
            <Typography
              variant="body2"
              sx={{ fontFamily: "monospace", mr: 0.5 }}
            >
              {t(URL_EXAMPLE_PLACEHOLDER)}?
            </Typography>
            {previewString
              ? previewString.split("&").map((param, i) => (
                  <Chip
                    key={i}
                    label={param}
                    size="small"
                    sx={{
                      fontFamily: "monospace",
                      fontSize: "0.7rem",
                      height: 24,
                    }}
                  />
                ))
              : null}
          </Box>
        </Box>
      </Box>

      <DialogActions>
        <ContainerModalActions
          propsSecondaryButton={{
            showButton: true,
            onClick: onCloseModal,
            text: BUTTON_GO_BACK,
          }}
          propsPrimaryButton={{
            showButton: true,
            text: MODAL_EDIT_URL_PARAMETERS_BUTTON_APPLY,
            type: "submit",
            form: "utm-modal-form",
            onClick: () => {},
          }}
        />
      </DialogActions>
    </ModalBasicLayout>
  );
};

export default ModalEditUrlParameters;
